import { ItemGroupResult, ItemResult } from '../models/importer-status';
import { PartnerContactService } from '../../partner/services/partner-contact.service';
import { PartnerContact } from '../../partner/models/partner-contact';
import { PartnerContactType } from '../../partner/models/partner-contact-type';
import { Injectable } from '@angular/core';


interface LookupEntry {
    key: string;
    groupName: string;
    itemName: (groups: ItemGroupResult[], item: ItemResult) => string;
}


@Injectable({
    providedIn: 'root'
})
export class ImportDisplayHelper {
    constructor(
        private partnerContactService: PartnerContactService) { }

    lookupTable: LookupEntry[] = [
        {
            key: 'AssemblyArtifact',
            groupName: 'Assembly artifacts',
            itemName: (groups, item) => `${item.LinkObjectFromXml?.AssemblyType} / ${item.LinkObjectFromXml?.Name} / ${item.LinkObjectFromXml?.AssemblyName}`
        },
        {
            key: 'ConcurrencyKey',
            groupName: 'Concurrency keys',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'TransportOutConfiguration',
            groupName: 'Outgoing locations',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'TransportInConfiguration',
            groupName: 'Incoming locations',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'HttpHandler',
            groupName: 'HTTP handlers',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'InitConfiguration',
            groupName: 'Init configurations',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'PartnerSystem',
            groupName: 'Partner systems',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ConfigurationKey',
            groupName: 'Configuration keys',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ConfigurationKeyUsage',
            groupName: 'Configuration key usages',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'DistributionConfiguration',
            groupName: 'Distribution Configuration',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name //Handled by importer
        },
        {
            key: 'DataField',
            groupName: 'Data fields',
            itemName: (groups, item) => item.LinkObjectFromXml?.DisplayName
        },
        {
            key: 'DataFieldChoice',
            groupName: 'Data field choices',
            itemName: (groups, item) => item.LinkObjectFromXml?.DisplayValue
        },
        {
            key: 'DataFieldGroup',
            groupName: 'Data field groups',
            itemName: (groups, item) => item.LinkObjectFromXml?.DisplayName
        },
        {
            key: 'DataFieldType',
            groupName: 'Data field types',
            itemName: (groups, item) => item.LinkObjectFromXml?.DataTypeName
        },
        {
            key: 'DistributionObject',
            groupName: 'Document types',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'DistributionObjectGroup',
            groupName: 'Document type groups',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'DocumentConfig',
            groupName: 'Document configurations',
            itemName: (groups, item) => `${this.getRefDisplayName(groups, item, 'DocumentType')} / ${item.LinkObjectFromXml.Direction} / ` +
                `${this.getRefDisplayName(groups, item, 'Format')} / ${this.getRefDisplayName(groups, item, 'Variant')} / ${this.getRefDisplayName(groups, item, 'VariantVersion')}`
        },
        {
            key: 'DocumentEncoding',
            groupName: 'Encodings',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'DoubletInterchangeType',
            groupName: 'Doublet interchange types',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ExceptionCategory',
            groupName: 'Error category',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
            //itemName: (groups, item) => item.LinkObjectFromXml?.ExceptionCategoryName
        },
        {
            key: 'ExceptionType',
            groupName: 'Error code',
            itemName: (groups, item) => `${item.LinkObjectFromXml?.ErrorCode}${this.getErrorCodeDescription(item.LinkObjectFromXml?.TechnicalDescription)}`
        },
        {
            key: 'FilenamingType',
            groupName: 'Filenaming types',
            itemName: (groups, item) => `${this.getRefDisplayName(groups, item, 'DistributionObject')} / ${item.LinkObjectFromXml?.FilenamingTypeName}`
        },
        {
            key: 'Format',
            groupName: 'Formats',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'FormatType',
            groupName: 'Format types',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'Gateway',
            groupName: 'Gateways',
            itemName: (groups, item) => item.LinkObjectFromXml?.GatewaysName
        },
        {
            key: 'IdentificationType',
            groupName: 'Identification types',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'IdentificationTypeQualifier',
            groupName: 'Identification type qualifiers',
            itemName: (groups, item) => `${item.LinkObjectFromXml?.Qualifier}`
        },
        {
            key: 'ItineraryStepDocumentConfig',
            groupName: 'Itinerary step configurations',
            itemName: (groups, item) => `[${this.getRefDisplayName(groups, item, 'DocumentConfig')}] / SortOrder: ${item.LinkObjectFromXml?.SortOrder} / [${this.getRefDisplayName(groups, item, 'ItineraryStep')}]`
        },
        {
            key: 'ItineraryStepDocumentConfigDistribution',
            groupName: 'Itinerary step configurations for distributions',
            itemName: (groups, item) => 'Itinerary step configuration for distribution'
        },
        {
            key: 'ItineraryStep',
            groupName: 'Itinerary steps',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ItineraryStepParameter',
            groupName: 'Itinerary step parameters',
            itemName: (groups, item) => `${this.getRefDisplayName(groups, item, 'ItineraryStep')} / ${this.getRefDisplayName(groups, item, 'DocumentConfig')}`
        },
        {
            key: 'InitializeStep',
            groupName: 'Initialize steps',
            itemName: (groups, item) => `${item.LinkObjectFromXml?.Name} / ${item.LinkObjectFromXml?.StepType} / ${item.LinkObjectFromXml?.StepKey}`
        },
        {
            key: 'TransportType',
            groupName: 'Transport types',
            itemName: (groups, item) => `${item.LinkObjectFromXml?.Name} / ${item.LinkObjectFromXml?.TransportType} / ${item.LinkObjectFromXml?.TransportKey}`
        },
        {
            key: 'TrackingFieldType',
            groupName: 'Tracking field types',
            itemName: (groups, item) => item.LinkObjectFromXml?.TypeName
        },
        {
            key: 'Variant',
            groupName: 'Variants',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'VariantVersion',
            groupName: 'Variant versions',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'XmlArtifact',
            groupName: 'XML artifacts',
            itemName: (groups, item) => `${item.LinkObjectFromXml?.XmlType} / ${item.LinkObjectFromXml?.Name} / ${item.LinkObjectFromXml?.Filename}`
        },
        {
            key: 'PromotedPropertyFilter',
            groupName: 'Xml Property Promotions',
            itemName: (groups, item) => `${item.LinkObjectFromXml?.Description} / ${item.LinkObjectFromXml?.RootNode} / ${item.LinkObjectFromXml?.RootNamespace}`
        },
        {
            key: 'PromotedPropertyField',
            groupName: 'Xml Property Promotion Fields',
            itemName: (groups, item) => `${this.getRefDisplayName(groups, item, 'PromotedPropertyFilter')} / ${item.LinkObjectFromXml?.PropertyName} / ${item.LinkObjectFromXml?.PropertyNamespace} / ${item.LinkObjectFromXml?.Xpath}`
        },
        {
            key: 'DocumentType',
            groupName: 'Document Type',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'DocumentTypeGroup',
            groupName: 'Document Type Group',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'DuplicateCheck',
            groupName: 'Duplicate Check',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ContentTypeGroup',
            groupName: 'Content Type Group',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ContentType',
            groupName: 'Content Type',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'DocumentTypeClassifierResolver',
            groupName: 'Document Type Classifier Resolver',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'DocumentTypeClassifier',
            groupName: 'Document Type Classifier',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'BatchingConfiguration',
            groupName: 'Batching Configuration',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ScheduleItem',
            groupName: 'Schedule',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ScheduleTrigger',
            groupName: 'Schedule Trigger',
            itemName: (groups, item) => `${this.getRefDisplayName(groups, item, 'ScheduleItem')} / Trigger XmlId=${item.XmlId}`
        },
        {
            key: 'DocumentConfigOutBatcher',
            groupName: 'Document Config Out Batcher',
            itemName: (groups, item) => `[Document Config: ${this.getRefDisplayName(groups, item, 'DocumentConfig')}]/ Batcher XmlId=${item.XmlId}`
        },
        {
            key: 'Distribution',
            groupName: 'Distribution',
            itemName: (groups, item) => `${this.getRefDisplayName(groups, item, 'DocumentConfig')}`
        },
        {
            key: 'Partner',
            groupName: 'Partner',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'PartnerIdentification',
            groupName: 'Partner Identification',
            itemName: (groups, item) => `[Partner: ${this.getRefDisplayName(groups, item, 'Partner')}]/ ID=${item.LinkObjectFromXml?.Value}`
        },
        {
            key: 'PartnerContactType',
            groupName: 'Partner Contact Type',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'PartnerContact',
            groupName: 'Partner Contact',
            itemName: (groups, item) => `[Partner: ${this.getRefDisplayName(groups, item, 'Partner')}]/ Name=${item.LinkObjectFromXml?.Name}`
        },
        {
            key: 'PartnerContactTypeRelation',
            groupName: 'Partner Contact Type Relation',
            itemName: (groups, item) => `[Partner: ${this.getRefDisplayName(groups, item, 'PartnerContact')}]` //handled by importer
        },
        {
            key: 'EdifactConfiguration',
            groupName: 'Edifact Configuration',
            itemName: (groups, item) => `[Partner: ${this.getRefDisplayName(groups, item, 'Partner')}]`
        },
        {
            key: 'X12Configuration',
            groupName: 'X12 Configuration',
            itemName: (groups, item) => `[Partner: ${this.getRefDisplayName(groups, item, 'Partner')}]`
        },
        {
            key: 'DuplicateCheckConfiguration',
            groupName: 'Duplicate Check Configuration',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ComparePlugin',
            groupName: 'Compare Rule',
            itemName: (groups, item) => item.LinkObjectFromXml?.Name
        },
        {
            key: 'ProxyPartnerRelation',
            groupName: 'Proxy Setting',
            itemName: (groups, item) => `[Proxy partner: ${this.getRefDisplayName(groups, item, 'Partner')}]`
        }
    ];

    getRefDisplayName(groups: ItemGroupResult[], item: ItemResult, objectType: string): string {
    const xmlId = item.References.find(e => e.ObjectType === objectType)?.XmlId;
    const refItem = groups.flatMap(e => e.ItemResults).find(e => e.XmlId === xmlId);

    if (!refItem) { return '(null)'; }

    const result = this.getDisplayName(groups, refItem.ObjectType, refItem);
    switch (result) {
        case null:
            return '(null)';
        case '':
            return ('blank');
        default:
            return result;
    }
}

    getReverseRefDisplayName(groups: ItemGroupResult[], item: ItemResult): string {
    const xmlId = item.XmlId;
    const refItem = groups.flatMap(e => e.ItemResults);
    const desiredObject = refItem.find(obj => obj.References.some(ref => ref.XmlId === xmlId));

    if (!desiredObject) { return '(null)'; }

    const result = this.getDisplayName(groups, desiredObject.ObjectType, desiredObject);
    switch (result) {
        case null:
            return '(null)';
        case '':
            return ('blank');
        default:
            return `[${desiredObject.ObjectType}] ${result}`;
    }
    }



    getDisplayName(groups: ItemGroupResult[], objectType: string, item?: ItemResult): string {
        const lookup = this.lookupTable.find(e => e.key === objectType);
        if (!lookup) { return item ? `[XmlId=${item.XmlId}]` : `[${objectType}]`; }

        var displayName = item ? lookup.itemName(groups, item) ?? `[XmlId=${item.XmlId}]` : lookup.groupName;
        if (item?.ImportDisplayName?.length > 0) {
            displayName = item?.ImportDisplayName;
        }
        switch (displayName) {
            case null:
                return '(null)';
            case '':
                return '(blank)';
            default:
                return displayName;
        }
    }

    getErrorCodeDescription(technicalDescription: string): string{
        let returnstring = "";
        if(typeof technicalDescription === 'string' && technicalDescription.length > 0) {
            returnstring = ": " + technicalDescription;
        }
        return returnstring;
    }
}
